import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";

import { themevals } from "theme/themevals";
import BasicModal from "components/ui/BasicModal";
import GooglePlaces from "components/forms/GooglePlaces";
import { useToast } from "components/toast";
import { createEstimate, generateEstimateQuote } from "api/InstantEstimate";
import { getInstantEstimateCombos, getInstantEstimateCombo } from "api/Companies";
import { beginLeadQuickQuote, finishLeadQuickQuote } from "api/Leads";

/**
 * New RFQ / New quote from a lead (LEADS_MODULE_SCOPE.md s.12.3).
 *
 * Products and services come from the company's Instant Estimate templates
 * (Company Settings > Instant Estimate) - the same list the header
 * estimate form uses, so the two can never disagree.
 *
 * - New RFQ: address, product, quantity -> the RFQ is created (every
 *   service priced) and opened; the service is chosen on the RFQ page, as
 *   for any RFQ.
 * - New quote: the user already knows what the lead wants, so the SERVICE
 *   is chosen here too. One click creates the RFQ behind the scenes and
 *   generates the quote for that service only, then opens the quote. The
 *   RFQ still exists and reads "quoted", exactly as after the manual path.
 *   If that service could not be priced for the site, the RFQ is opened
 *   instead with the reason.
 */

type Opt = { id: string; name: string };
type Template = { id: string; product: Opt; unit?: string | null; services: Opt[] };

const input: React.CSSProperties = { padding: "8px 10px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, fontSize: 13, fontFamily: "Poppins", width: "100%", boxSizing: "border-box", background: "#fff" };
const label: React.CSSProperties = { fontSize: 11, fontFamily: "Poppins", color: themevals.colors.dkgrey, display: "block", marginBottom: 4, textAlign: "left" };

/** Tolerates both shapes the template API has returned: services as rows or as {service}. */
function toTemplate(raw: any): Template | null {
  const product = raw?.product ?? raw?.templateProduct?.product;
  if (!product?.id) return null;
  const rows: any[] = raw?.services ?? raw?.templateProductServices ?? [];
  const services = rows
    .slice()
    .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
    .map((r) => r?.service ?? r)
    .filter((s) => s?.id && s?.name)
    .map((s) => ({ id: s.id, name: s.name }));
  return { id: raw.id, product: { id: product.id, name: product.name }, unit: product.unit ?? null, services };
}

export default function NewRfqDialog({
  leadId,
  leadName,
  contactName,
  contactEmail,
  intent,
  onClose,
}: {
  leadId: string;
  leadName: string;
  contactName: string | null;
  contactEmail: string | null;
  intent: "rfq" | "quote";
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [place, setPlace] = useState<{ address: any; latitude: number; longitude: number } | null>(null);
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState<"yds" | "sqft">("yds");

  useEffect(() => {
    (async () => {
      const res: any = await getInstantEstimateCombos();
      const items: any[] = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      let list = items.map(toTemplate).filter(Boolean) as Template[];
      // The list endpoint may omit services; fetch the detail for any that came back empty.
      list = await Promise.all(
        list.map(async (t) => {
          if (t.services.length) return t;
          const full: any = await getInstantEstimateCombo({ data: { id: t.id } });
          return toTemplate(full) ?? t;
        }),
      );
      setTemplates(list);
      if (list[0]) {
        setTemplateId(list[0].id);
        setServiceId(list[0].services[0]?.id ?? "");
      }
    })();
  }, []);

  const template = useMemo(() => templates.find((t) => t.id === templateId) ?? null, [templates, templateId]);
  const service = template?.services.find((s) => s.id === serviceId) ?? null;

  const fail = (m: string) => toast.add(m, "var(--theme-ui-colors-danger)");

  const submit = async () => {
    if (busy) return;
    if (!place?.latitude || !place?.longitude) return fail("Pick the site address from the list so it has coordinates.");
    if (!(Number(quantity) >= 1)) return fail("Quantity must be 1 or more.");
    if (!template) return fail("Pick a product.");
    if (intent === "quote" && !service) return fail("Pick the service the lead wants.");

    setBusy(true);
    setStep("Pricing the site\u2026");
    // New quote: the RFQ alert is skipped in favour of one "Quote ready"
    // alert naming the service. New RFQ keeps the normal RFQ alert.
    if (intent === "quote") await beginLeadQuickQuote(leadId);
    const res: any = await createEstimate({
      data: {
        clientId: leadId,
        address: { ...place.address, line1: place.address?.line1 || "Site", country: place.address?.country || "US" },
        latitude: place.latitude,
        longitude: place.longitude,
        quantity: Number(quantity),
        unit,
        productId: template.product.id,
      },
    });
    const rfqId = res?.quoteRequest?.id ?? res?.quoteRequestId;
    if (!rfqId) {
      setBusy(false);
      setStep(null);
      return fail(res?.message || res?.error || "The RFQ could not be created.");
    }

    if (intent === "rfq") {
      toast.add("RFQ created.", "var(--theme-ui-colors-success)");
      return navigate(`/quote-requests/${rfqId}`);
    }

    // New quote: find the priced line for the chosen service and quote it.
    const chosen = service;
    if (!chosen) {
      setBusy(false);
      setStep(null);
      return navigate(`/quote-requests/${rfqId}`);
    }
    const priced: any[] = Array.isArray(res?.services) ? res.services : [];
    const line =
      priced.find((s) => (s?.serviceId ?? s?.service?.id) === chosen.id) ??
      priced.find((s) => String(s?.name ?? "").trim().toLowerCase() === chosen.name.trim().toLowerCase());
    if (!line || !(Number(line.price) > 0)) {
      setBusy(false);
      setStep(null);
      toast.add(`${chosen.name} could not be priced for this site, so the RFQ is open instead.`, "var(--theme-ui-colors-danger)");
      return navigate(`/quote-requests/${rfqId}`);
    }

    setStep(`Creating the ${chosen.name} quote\u2026`);
    const quote: any = await generateEstimateQuote(res.id, [line.id]);
    setBusy(false);
    setStep(null);
    if (!quote?.id) {
      fail(quote?.message || quote?.error || "The quote could not be generated; the RFQ is open instead.");
      return navigate(`/quote-requests/${rfqId}`);
    }
    await finishLeadQuickQuote(leadId, quote.id, chosen.name);
    toast.add(`Quote created for ${chosen.name} \u2014 send it to the lead below.`, "var(--theme-ui-colors-success)");
    // Land where the next step is: the lead's Quotes tab with the Send
    // panel open for this quote, not the internal quote page.
    onClose();
    navigate(`/leads/${leadId}?tab=quotes&send=${quote.id}`);
  };

  const isQuote = intent === "quote";

  return (
    <BasicModal
      title={isQuote ? `New quote for ${leadName}` : `New RFQ for ${leadName}`}
      size="md"
      cancel_click={onClose}
      cancel_text="Cancel"
      confirm_text={busy ? step ?? "Working\u2026" : isQuote ? "Create quote" : "Create RFQ"}
      confirm_click={submit}
      submitting={busy}
    >
      <Box sx={{ fontSize: 13, fontFamily: "Poppins", color: themevals.colors.dkgrey, mb: 14, textAlign: "left" }}>
        {isQuote
          ? "For when you already know what the lead wants. The RFQ is created behind the scenes and the quote is generated for the service you pick, with the lead markup applied."
          : "The RFQ prices every service for this site; you choose the one to quote on the RFQ page."}
        {contactName || contactEmail ? ` Site contact defaults to ${[contactName, contactEmail].filter(Boolean).join(" \u00b7 ")}.` : ""}
      </Box>

      <Box sx={{ mb: 12, textAlign: "left" }}>
        <span style={label}>Site address</span>
        <GooglePlaces name="address" placeholder="Search the site address" onChange={(v: any) => setPlace(v)} />
      </Box>

      <Flex sx={{ gap: 10, mb: 12, flexWrap: "wrap" }}>
        <Box sx={{ flex: "2 1 200px" }}>
          <span style={label}>Product</span>
          <select
            style={input}
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              const t = templates.find((x) => x.id === e.target.value);
              setServiceId(t?.services[0]?.id ?? "");
            }}
          >
            {templates.length === 0 && <option value="">Loading…</option>}
            {templates.map((t) => <option key={t.id} value={t.id}>{t.product.name}</option>)}
          </select>
        </Box>
        {isQuote && (
          <Box sx={{ flex: "2 1 200px" }}>
            <span style={label}>Service</span>
            <select style={input} value={serviceId} onChange={(e) => setServiceId(e.target.value)} disabled={!template}>
              {(template?.services ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Box>
        )}
        <Box sx={{ flex: "1 1 100px" }}>
          <span style={label}>Quantity</span>
          <input style={input} type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </Box>
        <Box sx={{ flex: "1 1 120px" }}>
          <span style={label}>Unit</span>
          <select style={input} value={unit} onChange={(e) => setUnit(e.target.value as any)}>
            <option value="yds">cubic yards</option>
            <option value="sqft">square feet</option>
          </select>
        </Box>
      </Flex>

      {templates.length > 0 && templates.every((t) => t.services.length === 0) && isQuote && (
        <Box sx={{ fontSize: 12, fontFamily: "Poppins", color: "#A60000", textAlign: "left" }}>
          No services are set up on the Instant Estimate products (Company Settings &gt; Instant Estimate), so a quote cannot be generated directly. Use New RFQ.
        </Box>
      )}
    </BasicModal>
  );
}
